/**
 * ============================================================
 * Aussie Betta - Golden Micro-Peptide Tallow Serum
 * Product Detail Page - Interactive Scripts
 * File: assets/aussie-betta-pdp.js
 *
 * Includes:
 *  1. Scroll-triggered reveal animations (IntersectionObserver)
 *  2. KOL video carousel (responsive, arrow navigation)
 *  3. Video modal (open / close / keyboard ESC)
 * ============================================================
 */

(function () {
  'use strict';

  var kolOpenModal = null;

  /* ---
   * 1. SCROLL REVEAL
   * Adds .ab-visible to .ab-anim elements when they enter
   * the viewport (threshold 12%).
   * --- */
  function initScrollReveal() {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('ab-visible');
            observer.unobserve(entry.target); // fire once
          }
        });
      },
      { threshold: 0.12 }
    );

    document.querySelectorAll('.ab-pdp .ab-anim').forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---
   * 2. KOL VIDEO CAROUSEL
   * Scroll-snap based carousel with arrow navigation,
   * touch/swipe support and click vs drag detection.
   * --- */
  function initKolCarousel() {
    var track    = document.getElementById('ab-kol-track');
    var viewport = document.getElementById('ab-kol-viewport');
    var btnPrev  = document.getElementById('ab-kol-prev');
    var btnNext  = document.getElementById('ab-kol-next');

    if (!track || !viewport || !btnPrev || !btnNext) return;

    /* Prevent duplicate listeners if this function is ever called more than once. */
    if (viewport._kolCarouselInit) return;
    viewport._kolCarouselInit = true;

    var cards          = Array.from(track.querySelectorAll('.ab-kol__card'));
    var totalCards     = cards.length;
    var currentIdx     = 0;
    var activeCard     = null;
    var startX         = 0;
    var startY         = 0;
    var didDrag        = false;
    var DRAG_THRESHOLD = 10;

    var prefersReducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var EDGE_TOLERANCE = 2;

    function getScrollBehavior() {
      return prefersReducedMotion ? 'auto' : 'smooth';
    }

    function getMaxScrollLeft() {
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    }

    function isAtStart() {
      return viewport.scrollLeft <= EDGE_TOLERANCE;
    }

    function isAtEnd() {
      return viewport.scrollLeft >= getMaxScrollLeft() - EDGE_TOLERANCE;
    }

    function getNearestCardIndex() {
      var currentLeft = viewport.scrollLeft;
      var nearestIndex = 0;
      var nearestDistance = Infinity;

      cards.forEach(function (card, index) {
        var distance = Math.abs(card.offsetLeft - currentLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    function scrollToCard(index) {
      index = Math.max(0, Math.min(index, totalCards - 1));
      var card = cards[index];
      if (!card) return;
      currentIdx = index;
      var targetLeft = card.offsetLeft;
      var maxScrollLeft = getMaxScrollLeft();
      targetLeft = Math.max(0, Math.min(targetLeft, maxScrollLeft));
      viewport.scrollTo({ left: targetLeft, behavior: getScrollBehavior() });
      updateButtons();
    }

    function updateButtons() {
      btnPrev.disabled = isAtStart();
      btnNext.disabled = isAtEnd();
      btnPrev.setAttribute('aria-disabled', String(btnPrev.disabled));
      btnNext.setAttribute('aria-disabled', String(btnNext.disabled));
    }

    function onScrollEnd() {
      currentIdx = getNearestCardIndex();
      updateButtons();
    }

    btnPrev.addEventListener('click', function () {
      if (!isAtStart()) scrollToCard(getNearestCardIndex() - 1);
    });

    btnNext.addEventListener('click', function () {
      if (!isAtEnd()) scrollToCard(getNearestCardIndex() + 1);
    });

    if ('onscrollend' in viewport) {
      viewport.addEventListener('scrollend', onScrollEnd);
    } else {
      var scrollTimer;
      viewport.addEventListener('scroll', function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(onScrollEnd, 80);
      });
    }

    /* Debounced resize listener */
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var maxScrollLeft = getMaxScrollLeft();
        if (viewport.scrollLeft > maxScrollLeft) {
          viewport.scrollLeft = maxScrollLeft;
        }
        onScrollEnd();
      }, 120);
    });

    /* Click vs drag detection for modal (local state only) */
    function onPointerDown(e) {
      activeCard = e.currentTarget;
      startX = e.clientX;
      startY = e.clientY;
      didDrag = false;
      activeCard._suppressKolClick = false;
    }

    function onPointerMove(e) {
      if (!activeCard) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (!didDrag && Math.abs(dx) > DRAG_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        didDrag = true;
        activeCard._suppressKolClick = true;
      }
    }

    function onPointerUp() {
      activeCard = null;
    }

    function onPointerCancel() {
      activeCard = null;
      didDrag = false;
    }

    cards.forEach(function (card) {
      card.addEventListener('pointerdown', onPointerDown);
      card.addEventListener('click', function (e) {
        if (card._suppressKolClick) {
          e.preventDefault();
          e.stopPropagation();
          card._suppressKolClick = false;
          return;
        }
        var src = card.getAttribute('data-video');
        if (src && typeof kolOpenModal === 'function') kolOpenModal(src);
      });
    });

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { passive: true });
    window.addEventListener('pointercancel', onPointerCancel, { passive: true });

    updateButtons();
  }

  /* ---
   * 3. VIDEO MODAL
   * Clicking a .ab-kol__card opens the modal with the video
   * specified in data-video attribute.
   * --- */
  function initVideoModal() {
    var modal    = document.getElementById('ab-modal');
    var video    = document.getElementById('ab-modal-video');
    var backdrop = document.getElementById('ab-modal-backdrop');
    var closeBtn = document.getElementById('ab-modal-close');

    if (!modal || !video) return;

    function openModal(src) {
      video.src = src;
      modal.classList.add('ab-modal--open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      video.play().catch(function () {}); // autoplay may be blocked
    }

    function closeModal() {
      video.pause();
      video.src = '';
      modal.classList.remove('ab-modal--open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    /* Expose opener to KOL carousel without touching window state. */
    kolOpenModal = openModal;

    /* Close triggers */
    if (closeBtn)  closeBtn.addEventListener('click', closeModal);
    if (backdrop)  backdrop.addEventListener('click', closeModal);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('ab-modal--open')) {
        closeModal();
      }
    });
  }

  /* ---
   * INIT - wait for DOM ready
   * --- */
  function init() {
    initScrollReveal();
    initVideoModal();
    initKolCarousel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
